import React, { useState } from "react";

/**
 * 精度の推移。網羅率と的中率を運行日ごとに並べる。
 *
 * ## 色の割り当て
 *
 * サイト内の約束をそのまま使う。**緑＝観測できた事実、紫＝予測。**
 *
 *   網羅率（緑） … 実際に観測された便のうち予測に入っていた割合。分母は観測
 *   的中率（紫） … 予測した便のうち実際に観測された割合。分母は予測
 *
 * 分母がそれぞれ「観測」と「予測」なので、色の意味と一致する。
 * （検証: 緑 #047857 と紫 #7c3aed は P/D/T 型いずれでも ΔE 23.9 以上で分かれる）
 *
 * ## 縦軸を 0 から始めていない
 *
 * 値が 85〜95% に集まるので、0 から描くと差がまったく見えない。
 * **折れ線は 0 起点でなくてよい**（棒は駄目）。ただし軸の下端を必ず書く。
 */

const SERIES = [
  { key: "coverage_rate", label: "網羅率", color: "#047857", note: "観測された便のうち予測できた割合（主指標）" },
  { key: "hit_rate", label: "的中率", color: "#7c3aed", note: "予測した便のうち実際に観測された割合" },
];

const WIDTH = 720;
const HEIGHT = 240;
const PAD = { top: 16, right: 56, bottom: 30, left: 44 };

export default function AccuracyChart({ points, target }) {
  const [hover, setHover] = useState(null);

  // **測れなかった日も横軸に残す。** 抜くと線が両隣を直に結んでしまい、
  // その日にも値があったように見える。積んである日はそのまま並べて、
  // 値の無い系列だけ線を切る（path を見よ）。
  const usable = points;

  const measured = points.some((p) => p.coverage_rate !== null || p.hit_rate !== null);

  if (!measured) {
    return (
      <p className="anl-empty">
        まだ測れていません。毎晩04:30に前日ぶんが1日1行ずつ積まれます。
      </p>
    );
  }

  const values = usable.flatMap((p) =>
    SERIES.map((s) => p[s.key]).filter((v) => v !== null)
  );
  if (target !== null && target !== undefined) values.push(target);

  // 下端は 0.05 刻みで切り下げる。**必ず軸に書くこと。**
  const low = Math.min(0.9, Math.floor((Math.min(...values) - 0.03) * 20) / 20);
  const high = 1;

  const innerW = WIDTH - PAD.left - PAD.right;
  const innerH = HEIGHT - PAD.top - PAD.bottom;

  const x = (i) =>
    PAD.left + (usable.length === 1 ? innerW / 2 : (i * innerW) / (usable.length - 1));
  const y = (v) => PAD.top + innerH - ((v - low) / (high - low)) * innerH;

  const ticks = [];
  for (let v = Math.ceil(low * 20) / 20; v <= high + 1e-9; v += 0.05) {
    ticks.push(Math.round(v * 100) / 100);
  }

  // 日付ラベルは詰め込まない。多くても 6 個
  const labelEvery = Math.max(1, Math.ceil(usable.length / 6));

  function path(key) {
    let d = "";
    let pen = false;

    usable.forEach((p, i) => {
      if (p[key] === null || p[key] === undefined) {
        // **測れなかった日は線を切る。** 繋ぐと値があったように見える
        pen = false;
        return;
      }
      d += `${pen ? "L" : "M"}${x(i)} ${y(p[key])} `;
      pen = true;
    });

    return d.trim();
  }

  function onMove(e) {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * WIDTH;
    const i = Math.round(((px - PAD.left) / innerW) * (usable.length - 1));

    setHover(Math.max(0, Math.min(usable.length - 1, i)));
  }

  const active = hover === null ? null : usable[hover];

  return (
    <div className="anl-chart">
      <div className="anl-legend">
        {SERIES.map((s) => (
          <span key={s.key} className="anl-legend__item" title={s.note}>
            <span className="anl-legend__swatch" style={{ background: s.color }} />
            {s.label}
          </span>
        ))}
        {target !== null && target !== undefined && (
          <span className="anl-legend__item">
            <span className="anl-legend__swatch anl-legend__swatch--target" />
            目標{Math.round(target * 100)}%
          </span>
        )}
      </div>

      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="anl-chart__svg"
        role="img"
        aria-label={`網羅率と的中率の推移（${usable[0].service_date}〜${usable[usable.length - 1].service_date}）`}
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
      >
        {ticks.map((t) => (
          <g key={t}>
            <line
              x1={PAD.left} x2={WIDTH - PAD.right} y1={y(t)} y2={y(t)}
              stroke="#d1fae5" strokeWidth="1"
            />
            <text x={PAD.left - 6} y={y(t) + 4} textAnchor="end" className="anl-chart__tick">
              {Math.round(t * 100)}%
            </text>
          </g>
        ))}

        {/* 目標線。一般公開の判断はここを超えてから */}
        {target !== null && target !== undefined && (
          <line
            x1={PAD.left} x2={WIDTH - PAD.right} y1={y(target)} y2={y(target)}
            stroke="#6b7280" strokeWidth="1.5" strokeDasharray="5 4"
          />
        )}

        {usable.map((p, i) =>
          i % labelEvery === 0 ? (
            <text
              key={p.service_date}
              x={x(i)} y={HEIGHT - 10} textAnchor="middle"
              className="anl-chart__tick"
            >
              {p.service_date.slice(5)}
            </text>
          ) : null
        )}

        {hover !== null && (
          <line
            x1={x(hover)} x2={x(hover)} y1={PAD.top} y2={PAD.top + innerH}
            stroke="#9ca3af" strokeWidth="1"
          />
        )}

        {SERIES.map((s) => (
          <path
            key={s.key}
            d={path(s.key)}
            fill="none"
            stroke={s.color}
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ))}

        {SERIES.map((s) =>
          usable.map((p, i) =>
            p[s.key] === null || p[s.key] === undefined ? null : (
              <circle
                key={`${s.key}-${p.service_date}`}
                cx={x(i)} cy={y(p[s.key])}
                r={hover === i ? 5 : 3.5}
                fill={s.color}
                stroke="#fff"
                strokeWidth="2"
              />
            )
          )
        )}

        {/* いちばん新しい値だけ直接書く。全部に数字を振ると読めなくなる */}
        {SERIES.map((s) => {
          const last = [...usable].reverse().find((p) => p[s.key] !== null && p[s.key] !== undefined);
          if (!last) return null;
          const i = usable.indexOf(last);

          return (
            <text
              key={`label-${s.key}`}
              x={x(i) + 8} y={y(last[s.key]) + 4}
              className="anl-chart__value"
            >
              {Math.round(last[s.key] * 1000) / 10}%
            </text>
          );
        })}
      </svg>

      {active && (
        <div className="anl-chart__tip">
          <strong>{active.service_date}</strong>
          {SERIES.map((s) => (
            <span key={s.key}>
              <span className="anl-legend__swatch" style={{ background: s.color }} />
              {s.label} {active[s.key] === null ? "—" : `${Math.round(active[s.key] * 1000) / 10}%`}
            </span>
          ))}
          <span>台日{active.bus_days} · 取り違え{active.misidentified}</span>
        </div>
      )}

      <p className="anl-note">
        縦軸は{Math.round(low * 100)}%から。値が上のほうに集まるため0からは描いていません。
      </p>
    </div>
  );
}
